
/**
 * @private
 * @type {Array.<string>}
 */
const HEX = [
    '00','01','02','03','04','05','06','07','08','09','0A','0B','0C','0D','0E','0F',
    '10','11','12','13','14','15','16','17','18','19','1A','1B','1C','1D','1E','1F',
    '20','21','22','23','24','25','26','27','28','29','2A','2B','2C','2D','2E','2F',
    '30','31','32','33','34','35','36','37','38','39','3A','3B','3C','3D','3E','3F',
    '40','41','42','43','44','45','46','47','48','49','4A','4B','4C','4D','4E','4F',
    '50','51','52','53','54','55','56','57','58','59','5A','5B','5C','5D','5E','5F',
    '60','61','62','63','64','65','66','67','68','69','6A','6B','6C','6D','6E','6F',
    '70','71','72','73','74','75','76','77','78','79','7A','7B','7C','7D','7E','7F',
    '80','81','82','83','84','85','86','87','88','89','8A','8B','8C','8D','8E','8F',
    '90','91','92','93','94','95','96','97','98','99','9A','9B','9C','9D','9E','9F',
    'A0','A1','A2','A3','A4','A5','A6','A7','A8','A9','AA','AB','AC','AD','AE','AF',
    'B0','B1','B2','B3','B4','B5','B6','B7','B8','B9','BA','BB','BC','BD','BE','BF',
    'C0','C1','C2','C3','C4','C5','C6','C7','C8','C9','CA','CB','CC','CD','CE','CF',
    'D0','D1','D2','D3','D4','D5','D6','D7','D8','D9','DA','DB','DC','DD','DE','DF',
    'E0','E1','E2','E3','E4','E5','E6','E7','E8','E9','EA','EB','EC','ED','EE','EF',
    'F0','F1','F2','F3','F4','F5','F6','F7','F8','F9','FA','FB','FC','FD','FE','FF'
];

export default class Color {
    /**
     * @param {number} r Floating point value between 0-1.
     * @param {number} g Floating point value between 0-1.
     * @param {number} b Floating point value between 0-1.
     * @param {number} a Floating point value between 0-1.
     */
    constructor(r, g, b, a) {
        /**
         * @private
         * @type {number}
         */
        this.r = r;
        /**
         * @private
         * @type {number}
         */
        this.g = g;
        /**
         * @private
         * @type {number}
         */
        this.b = b;
        /**
         * @type {number}
         */
        this.a = a;

        /**
         * @private
         * @type {string}
         */
        this.fillColor = null;

        this.clamp();
    }

    /**
     * Creates a color using the RGBA color integer (eg. 0xc0c0c0FF).
     * @param {number} rgba 
     * @returns {Color}
     */
    static createColor(rgba) {
        const red = (rgba & 0xFF000000) >> 24;
        const green = (rgba & 0x00FF0000) >> 16;
        const blue = (rgba & 0x0000FF00) >> 8;
        const alpha = rgba & 0x000000FF;

        return new Color(red / 255, green / 255, blue / 255, alpha / 255);
    }

    /**
     * @param {Color} color
     */
    set(color) {
        this.r = color.r;
        this.g = color.g;
        this.b = color.b;
        this.a = color.a;

        //this.clamp();
        this.fillColor = null;
    }

    /**
     * @param {number} alpha In the range of 0..1
     */
    setAlpha(alpha) {
        this.a = alpha;
    }

    /**
     * @param {number} alpha In the range of 0..1
     */
    addAlpha(alpha) {
        this.a += alpha;
        if (this.a > 1)  {
            this.a = 1;
        }
    }

    reset() {
        this.r = 0;
        this.g = 0;
        this.b = 0;
        this.a = 0;
    }
    
    /**
     * @param {number} r 
     * @param {number} g 
     * @param {number} b 
     * @param {number} a 
     */
    setRgba(r, g, b, a) {
        this.r = r;
        this.g = g;
        this.b = b;
        this.a = a;

        this.clamp();
        this.fillColor = null;
    }
    
    /**
     * @param {number} r 
     * @param {number} g 
     * @param {number} b 
     * @param {number} a 
     */
    setRgbaFast(r, g, b, a) {
        this.r = r;
        this.g = g;
        this.b = b;
        this.a = a;
        this.fillColor = null;
    }

    resetFast() {
        this.r = 0;
        this.g = 0;
        this.b = 0;
        this.a = 0;
        this.fillColor = null;
    }

    /**
     * @param {number} value 
     */
    mul(value) {
		this.r *= value;
		this.g *= value;
		this.b *= value;
		this.a *= value;
        
		this.clamp();
        this.fillColor = null;
	}

    /**
     * @param {Color} color
     */
	add(color) {
		this.r += color.r;
		this.g += color.g;
		this.b += color.b;
		this.a += color.a;

		this.clamp();
        this.fillColor = null;
	}
    /**
     * @param {Color} color
     */
	addFast(color) {
		this.r += color.r;
		this.g += color.g;
		this.b += color.b;
		this.a += color.a;

        this.fillColor = null;
	}

    /**
     * @param {number} r 
     * @param {number} g 
     * @param {number} b 
     * @param {number} a 
     */
	addRgba(r, g, b, a) {
		this.r += r;
		this.g += g;
		this.b += b;
		this.a += a;

		this.clamp();
        this.fillColor = null;
	}

    /**
     * @param {number} r 
     * @param {number} g 
     * @param {number} b 
     * @param {number} a 
     */
	addFastRgba(r, g, b, a) {
		this.r += r;
		this.g += g;
		this.b += b;
		this.a += a;

        this.fillColor = null;
	}

    /**
     * @returns {string}
     */
    getFillColor() {
        if (!this.fillColor) {
            this.fillColor = this.createFillColor();
        }
        return this.fillColor;        
    }

    /**
     * @private
     * @returns {string}
     */
    createFillColor() {
        // Invert the alpha, because our lighting fill color is really a shadow that we are drawing
        // over the tile.
        const aa = ((1 - this.a) * 255) | 0,
            rr = (this.r * 255) | 0,
            gg = (this.g * 255) | 0,
            bb = (this.b * 255) | 0;

        return '#' + HEX[rr] + HEX[gg] + HEX[bb] + HEX[aa];        
    }

    clamp () {
		if (this.r < 0) {
            this.r = 0;
        }
		else if (this.r > 1) {
            this.r = 1;
        }

		if (this.g < 0) {
			this.g = 0;
        }
		else if (this.g > 1) {
            this.g = 1;
        }

		if (this.b < 0) {
			this.b = 0;
        }
		else if (this.b > 1) {
            this.b = 1;
        }

		if (this.a < 0) {
			this.a = 0;
        }
		else if (this.a > 1)  {
            this.a = 1;
        }
	}

}